import { jsx } from 'react/jsx-runtime';
import { isSsr, getDataEventParams } from '@lemonade-hq/cdk';
import { EUROPEAN_GDPR_COUNTRIES } from '@lemonade-hq/lemonation';
import { NOOP } from '@lemonade-hq/ts-helpers';
import isbot from 'isbot';
import { createContext, useState, useRef, useMemo, useEffect, useCallback, useContext } from 'react';
import { useCookies } from 'react-cookie';
import TagManager from 'react-gtm-module';
import { getEnv } from '../helpers/infra.esm.js';
import { getUUID, trackPage, trackEvent, trackDataForGtm, getDefaultAnalyticsProps, getAnalyticsDataObject, alias } from '../helpers/tracking.esm.js';
import { useComplianceProvider } from './ComplianceProvider.esm.js';
import { useI18nContext } from './I18nProvider.esm.js';
import { Client } from '../helpers/client/client.esm.js';
import { Cookie } from '../config/cookies.esm.js';

/* eslint-disable @typescript-eslint/naming-convention */ function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var ANALYTICS_INIT_DELAY = 300;
// INTERNAL FUNCTIONS *************************************
function trackOnClick(param) {
    var target = param.target;
    //  eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (target != null && _instanceof(target, HTMLElement) && target.dataset != null && target.dataset.event != null) {
        var dataEventParams = getDataEventParams(target);
        trackEvent(target.dataset.event, dataEventParams);
    }
}
function trackAnalyticsEvent(url, eventName) {
    var props = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {}, anonymously = arguments.length > 3 ? arguments[3] : void 0;
    // ATM we don't want to track at all when anonymously
    if (anonymously === true) {
        return;
    }
    var eventProps = _object_spread({}, getDefaultAnalyticsProps(), props);
    if (eventProps.statsd != null && eventProps.statsd.length > 0) {
        eventProps.statsd = "".concat(getEnv(), ".business.web.").concat(props.statsd);
    }
    var opts = getAnalyticsDataObject({
        eventName: eventName,
        props: eventProps
    }, anonymously);
    var body = {
        event_name: eventName,
        opts: opts
    };
    Client.post(url, body).catch(NOOP);
}
function identify(id, traits) {
    if (typeof id === "string") {
        if (traits == null) {
            var _window_analytics, _window_analytics_identify;
            (_window_analytics = window.analytics) === null || _window_analytics === void 0 ? void 0 : (_window_analytics_identify = _window_analytics.identify) === null || _window_analytics_identify === void 0 ? void 0 : _window_analytics_identify.call(_window_analytics, id);
        } else {
            var _window_analytics1, _window_analytics_identify1;
            (_window_analytics1 = window.analytics) === null || _window_analytics1 === void 0 ? void 0 : (_window_analytics_identify1 = _window_analytics1.identify) === null || _window_analytics_identify1 === void 0 ? void 0 : _window_analytics_identify1.call(_window_analytics1, id, traits);
        }
    } else {
        var _window_analytics2, _window_analytics_identify2;
        var newTraits = id;
        (_window_analytics2 = window.analytics) === null || _window_analytics2 === void 0 ? void 0 : (_window_analytics_identify2 = _window_analytics2.identify) === null || _window_analytics_identify2 === void 0 ? void 0 : _window_analytics_identify2.call(_window_analytics2, newTraits);
    }
}
function register(userConfig) {
    var userId = userConfig.userId, email = userConfig.email, givenName = userConfig.givenName, familyName = userConfig.familyName;
    alias(userId);
    var props = _object_spread({
        created_at: Math.round(Date.now() / 1000),
        id: userId,
        first_name: givenName,
        last_name: familyName,
        email: email
    }, getDefaultAnalyticsProps());
    identify(userId, props);
}
// CONTEXT *************************************************
// istanbul ignore next
var AnalyticsContext = /*#__PURE__*/ createContext({
    activateTracking: function() {},
    trackEvent: function() {},
    trackAnalyticsEvent: function() {},
    trackDataForGtm: function() {},
    setAdditionalProps: function() {},
    canManageCookies: false,
    identify: function() {},
    register: function() {},
    cookieBannerAccepted: false
});
// PROVIDER ************************************************
var AnalyticsProvider = function(param) {
    var enableGtm = param.enableGtm, gtmId = param.gtmId, segmentKey = param.segmentKey, userAgent = param.userAgent, children = param.children;
    var region = useI18nContext().region;
    var _useComplianceProvider = useComplianceProvider(), cookieTypesEnabledList = _useComplianceProvider.cookieTypesEnabledList, wasCookieBannerAcceptedInteracted = _useComplianceProvider.wasCookieBannerAcceptedInteracted;
    var _useCookies = _sliced_to_array(useCookies([
        Cookie.CookieBannerAccepted,
        Cookie.EnableCookie,
        Cookie.CookieTypesEnabled
    ]), 1), cookies = _useCookies[0];
    var isTrackingViaCookiesEnabled = cookies[Cookie.EnableCookie] === "true";
    var _useState = _sliced_to_array(useState(false), 2), trackingActivated = _useState[0], setTrackingActivated = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), gtmActivated = _useState1[0], setGtmActivated = _useState1[1];
    var _useState2 = _sliced_to_array(useState({}), 2), additionalProps = _useState2[0], setAdditionalProps = _useState2[1];
    var analyticsLoaded = useRef(false);
    var storedTrackingInfo = useRef({
        traits: {}
    });
    var enableCookies = useMemo(function() {
        return isSsr() ? false : isTrackingViaCookiesEnabled || !EUROPEAN_GDPR_COUNTRIES.includes(region);
    }, [
        region,
        isTrackingViaCookiesEnabled
    ]);
    var isBotRequest = useMemo(function() {
        return isbot(userAgent !== null && userAgent !== void 0 ? userAgent : isSsr() ? "" : window.navigator.userAgent);
    }, [
        userAgent
    ]);
    useEffect(function() {
        document.body.addEventListener("click", trackOnClick);
        return function() {
            document.body.removeEventListener("click", trackOnClick);
        };
    }, []);
    var initTracking = useCallback(function() {
        var uuid = getUUID();
        var traits = _object_spread({}, uuid !== "" && {
            l_uuid: uuid
        }, storedTrackingInfo.current.traits);
        if (storedTrackingInfo.current.id != null) {
            register(_object_spread({
                userId: storedTrackingInfo.current.id
            }, storedTrackingInfo.current.traits));
        } else {
            identify(traits);
        }
    }, []);
    var activateGtm = useCallback(function() {
        if (enableGtm && gtmId != null) {
            setGtmActivated(true);
            TagManager.initialize({
                gtmId: gtmId
            }); // eslint-disable-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            TagManager.dataLayer({
                dataLayer: {
                    country: region.toUpperCase()
                }
            }); // eslint-disable-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        }
    }, [
        enableGtm,
        gtmId,
        region
    ]);
    var activateTracking = useCallback(function() {
        if (cookieTypesEnabledList.includes("performance")) {
            setTrackingActivated(true);
            var analytics = window.analytics;
            if (!analyticsLoaded.current && (analytics === null || analytics === void 0 ? void 0 : analytics.load) != null && segmentKey != null) {
                analyticsLoaded.current = true;
                analytics.load(segmentKey);
            }
            trackPage();
            initTracking();
        }
        if (cookieTypesEnabledList.includes("analytics")) {
            TagManager.dataLayer({
                dataLayer: {
                    event: "consent_given"
                }
            }); // eslint-disable-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        }
    }, [
        initTracking,
        segmentKey,
        cookieTypesEnabledList
    ]);
    useEffect(function() {
        var activateGtmTimer = null;
        if (!isBotRequest && !gtmActivated) {
            activateGtmTimer = setTimeout(activateGtm, ANALYTICS_INIT_DELAY);
        }
        return function() {
            if (activateGtmTimer) {
                clearTimeout(activateGtmTimer);
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(function() {
        var activateTrackingTimer = null;
        if (!isBotRequest && enableCookies && !trackingActivated) {
            activateTrackingTimer = setTimeout(activateTracking, ANALYTICS_INIT_DELAY);
        }
        return function() {
            if (activateTrackingTimer) {
                clearTimeout(activateTrackingTimer);
            }
        };
    }, [
        enableCookies
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    var internalTrackEvent = useCallback(function(eventName, props) {
        trackEvent(eventName, _object_spread({}, additionalProps, props), !trackingActivated);
    }, [
        additionalProps,
        trackingActivated
    ]);
    var internalTrackAnalyticsEvent = useCallback(function(baseUrl, eventName, props) {
        trackAnalyticsEvent(baseUrl, eventName, _object_spread({}, additionalProps, props), !trackingActivated);
    }, [
        additionalProps,
        trackingActivated
    ]);
    var internalRegister = useCallback(function() {
        for(var _len = arguments.length, props = new Array(_len), _key = 0; _key < _len; _key++){
            props[_key] = arguments[_key];
        }
        if (!trackingActivated) {
            var _props = _sliced_to_array(props, 1), userConfig = _props[0];
            var userId = userConfig.userId, restTraits = _object_without_properties(userConfig, [
                "userId"
            ]);
            storedTrackingInfo.current.id = userId;
            storedTrackingInfo.current.traits = _object_spread({}, storedTrackingInfo.current.traits, restTraits);
            return;
        }
        register.apply(void 0, _to_consumable_array(props));
    }, [
        trackingActivated
    ]);
    var internalIdentify = useCallback(function(id, traits) {
        if (!trackingActivated) {
            if (typeof id === "string") {
                storedTrackingInfo.current.id = id;
                storedTrackingInfo.current.traits = _object_spread({}, storedTrackingInfo.current.traits, traits);
            } else {
                storedTrackingInfo.current.traits = _object_spread({}, storedTrackingInfo.current.traits, id);
            }
            return;
        }
        if (typeof id === "string") {
            identify(id, traits);
        } else {
            identify(id);
        }
    }, [
        trackingActivated
    ]);
    var value = useMemo(function() {
        var getIsCookieBannerAccepted = function() {
            if (cookies[Cookie.CookieBannerAccepted] == null) return null;
            return cookies[Cookie.CookieBannerAccepted] === "true";
        };
        return {
            activateTracking: activateTracking,
            trackEvent: internalTrackEvent,
            trackAnalyticsEvent: internalTrackAnalyticsEvent,
            trackDataForGtm: trackDataForGtm,
            setAdditionalProps: setAdditionalProps,
            canManageCookies: Boolean(wasCookieBannerAcceptedInteracted),
            identify: internalIdentify,
            register: internalRegister,
            cookieBannerAccepted: getIsCookieBannerAccepted()
        };
    }, [
        activateTracking,
        internalTrackEvent,
        internalTrackAnalyticsEvent,
        wasCookieBannerAcceptedInteracted,
        internalIdentify,
        internalRegister,
        cookies
    ]);
    return /*#__PURE__*/ jsx(AnalyticsContext.Provider, {
        value: value,
        children: children
    });
};
var useAnalyticsContext = function() {
    return useContext(AnalyticsContext);
};

export { ANALYTICS_INIT_DELAY, AnalyticsContext, AnalyticsProvider, useAnalyticsContext };
